import { submitFormJsonWithAbort } from 'widgets/toolbox/ajax';

import { TBackInStockInstance } from 'widgets/forms/ajax/BackInStock';
import { TProductActionButtonInstance } from 'core/product/ProductActionButton';
import { TProductVariantSelectInstance } from 'widgets/product/ProductVariantSelect';
import { TProduct } from 'types/models/product';
import { dialogMgr } from 'widgets/toolbox/dialogMgr';
import { PAGE_LOAD_ACTION_TYPES } from 'core/product/PageLoadActionProcessor';

/**
 * @description Base AvailabilityReminderModalMixin implementation
 * @param DrawerDialog Base widget for extending
 * @returns AvailabilityReminderModalMixin class
 */
function AvailabilityReminderModalMixinClassCreator(DrawerDialog: import('core/global/drawerDialog').TDrawerDialog) {
    class AvailabilityReminderModalMixin extends DrawerDialog {
        modalSuccessTitle?: string;

        currentProductId?: string;

        prefs() {
            return {
                backInStock: 'backInStock',
                storeAvailabilityBtn: 'storeAvailabilityBtn',
                attributeSelections: <{ [key: string]: boolean }>{},
                unselectedAttributeHighlighClass: 'm-highlight',
                ...super.prefs()
            };
        }

        /**
         * @description Widget initialization logic
         * @listens module:events#drawershow
         */
        init() {
            super.init();
            this.eventBus().on('availabilityReminderModal.show', 'showAvailabilityReminderModal');
        }

        /**
         * @description Show modal dialog with availability reminder content
         * @param templateData Data for rendering modal
         * @param cb Callback function to execute after modal is shown
         * @returns Promise that resolves when modal is shown
         */
        showAvailabilityReminderModal(templateData?: Record<string, unknown>, cb?: () => void): Promise<void> {
            this.modalSuccessTitle = (templateData?.modalSuccessTitle ?? '') as string;

            const shouldSignInToSubscribe = (templateData?.shouldSignInToSubscribe ?? false) as boolean;

            if (shouldSignInToSubscribe && templateData?.signInModalUrl) {
                const signInData = {
                    url: templateData?.signInModalUrl,
                    headerText: templateData?.signInModalTitle,
                    wrapperClasses: 'm-sign_in_to_buy',
                    pageLoadAction: {
                        type: PAGE_LOAD_ACTION_TYPES.AVAILABILITY_REMINDER,
                        data: templateData || {}
                    }
                };

                this.eventBus().emit('signInToBuyModal.show', signInData);

                return new Promise(() => {}); // This dialog will never show
            } else {
                return this.showModal(templateData, cb);
            }
        }

        /**
         * @description Render product attributes
         * @param product Product object
         */
        renderVariationAttributes(product: TProduct): void {
            const { variationAttributes } = product;

            if (variationAttributes?.length) {
                variationAttributes.forEach(variationAttribute => {
                    variationAttribute.values = variationAttribute.values.map(value => {
                        return {
                            ...value,
                            selected: value.selected,
                            disabled: !value.selectable
                        };
                    });

                    this.getById('attr-' + variationAttribute.attributeId, attribute => {
                        attribute.render(
                            'template',
                            {
                                attr: variationAttribute
                            },
                            attribute.ref('container')
                        );
                    });
                });
            }
        }

        /**
         * @description Handles change of product variant select widget
         * @param selectWidget Product variant select widget instance
         */
        changeAttribute(selectWidget: TProductVariantSelectInstance): Promise<null | Record<string, unknown>> {
            const selected = selectWidget.getSelectedVariations();

            if (!selected || selected.data('attrIsSelected')) {
                return Promise.resolve(null);
            }

            const requestUrl = selected.data('attrUrl') as string;
            const requestObject = submitFormJsonWithAbort(requestUrl, undefined, 'GET', true);

            selectWidget.ref('content').toggleClass(this.prefs().classesLoading, true).attr('aria-busy', 'true');

            return requestObject.promise
                .then(response => {
                    if (!response?.success) {
                        return null;
                    }

                    const { product } = response;

                    this.currentProductId = product?.id || '';

                    if (product?.attributeSelections) {
                        this.setPref('attributeSelections', product.attributeSelections);
                    }

                    this.getById<TBackInStockInstance, void>(this.prefs().backInStock, backInStock => {
                        backInStock.updateProductId(this.currentProductId);
                    });

                    this.getById<TProductActionButtonInstance, void>(this.prefs().storeAvailabilityBtn, storeAvailabilityBtn => {
                        storeAvailabilityBtn.updateProductId(this.currentProductId);
                    });

                    this.renderVariationAttributes(product);

                    return response;
                })
                .finally(() => {
                    selectWidget.ref('content').toggleClass(this.prefs().classesLoading, false);
                });
        }

        /**
         * @description Handles back in stock form submit
         */
        handleBackInStockFormSubmit(): void {
            this.ref('formContainer').hide();
            this.ref('resultContainer').show();

            if (this.modalSuccessTitle) {
                this.ref(this.prefs().refTitle).setText(this.modalSuccessTitle);
            }
        }

        /**
         * @description Handles back in stock form empty product ID
         */
        handleBackInStockFormEmptyProductId(): void {
            this.highlightUnselectedAttributes();
        }

        /**
         * @description Highlights unselected attribute labels
         */
        highlightUnselectedAttributes(): void {
            Object.entries(this.prefs().attributeSelections).forEach(([attribute, isSelected]) => {
                this.getById(`attr-${attribute}`, widget => {
                    if (isSelected) {
                        widget.ref('container').removeClass(this.prefs().unselectedAttributeHighlighClass);
                    } else {
                        widget.ref('container').addClass(this.prefs().unselectedAttributeHighlighClass);
                    }
                });
            });
        }

        /**
         * @description emit after show modal event
         */
        afterShowModal() {
            super.afterShowModal();

            this.eventBus().emit('availabilityReminderModal.showed');
        }

        /**
         * @description close all modals
         */
        closeModal() {
            super.closeModal();
            dialogMgr.closeDialog();
        }
    }

    return AvailabilityReminderModalMixin;
}

export type TAvailabilityReminderModalMixin = ReturnType<typeof AvailabilityReminderModalMixinClassCreator>;

export type TAvailabilityReminderModalMixinInstance = InstanceType<TAvailabilityReminderModalMixin>;

export default AvailabilityReminderModalMixinClassCreator;
